import { useState } from "react";
import CopyCode from "../../CopyCode/CopyCode";

const BadgeLabel = () => {
  return (
    <div className="flex flex-col gap-y-10 p-6">
      {/* BASIC BADGES */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-white mb-6">Badges & Labels</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Basic Badge</label>
            <div className="flex flex-wrap gap-2">
              <span className="px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded">Default</span>
              <span className="px-2 py-1 bg-slate-600 text-slate-200 text-xs rounded">Badge</span>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<span className="px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded">
  Badge
</span>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Colored Badges</label>
            <div className="flex flex-wrap gap-2">
              {[{bg: "bg-amber-900", text: "text-amber-300", label: "Amber"}, {bg: "bg-slate-700", text: "text-slate-300", label: "Slate"}, {bg: "bg-orange-900", text: "text-orange-300", label: "Orange"}].map(badge => (
                <span key={badge.label} className={`px-2 py-1 ${badge.bg} ${badge.text} text-xs rounded`}>{badge.label}</span>
              ))}
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<span className="px-2 py-1 bg-amber-900 text-amber-300 rounded">Amber</span>
<span className="px-2 py-1 bg-slate-700 text-slate-300 rounded">Slate</span>
<span className="px-2 py-1 bg-amber-900 text-amber-300 rounded">Pink</span>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Pill Badges</label>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-amber-600 text-amber-100 text-xs rounded-full">New</span>
              <span className="px-3 py-1 bg-green-600 text-green-100 text-xs rounded-full">Active</span>
              <span className="px-3 py-1 bg-amber-600 text-amber-100 text-xs rounded-full">New</span>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<span className="px-3 py-1 bg-amber-600 text-amber-100 text-xs rounded-full">
  New
</span>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Icon Badges</label>
            <div className="flex flex-wrap gap-2">
              <span className="flex items-center gap-1 px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded">
                 Success
              </span>
              <span className="flex items-center gap-1 px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded">
                 Warning
              </span>
              <span className="flex items-center gap-1 px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded">
                 Error
              </span>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<span className="flex items-center gap-1 px-2 py-1 rounded">
   Success
</span>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Status Badges</label>
            <div className="flex flex-wrap gap-2">
              {[{dot: "", label: "Online", color: "bg-green-900 text-green-300"}, {dot: "", label: "Away", color: "bg-yellow-900 text-yellow-300"}, {dot: "", label: "Offline", color: "bg-slate-700 text-slate-300"}].map(status => (
                <span key={status.label} className={`px-2 py-1 ${status.color} text-xs rounded flex items-center gap-1`}>
                  <span>{status.dot}</span> {status.label}
                </span>
              ))}
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<span className="bg-green-900 px-2 py-1 rounded">
   Online
</span>
<span className="bg-yellow-900 px-2 py-1 rounded">
   Away
</span>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Count Badges</label>
            <div className="flex items-center gap-3">
              <div className="relative">
                <button className="p-2 bg-slate-700 rounded hover:bg-slate-600"></button>
                <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">3</span>
              </div>
              <div className="relative">
                <button className="p-2 bg-slate-700 rounded hover:bg-slate-600"></button>
                <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">5</span>
              </div>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="relative">
  <button>Icon</button>
  <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full">
    3
  </span>
</div>`} />
          </div>
        </div>
      </div>

      {/* ADVANCED BADGES */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-amber-400 mb-6">Advanced Badges</h2>
        <div className="grid grid-cols-1 gap-4">
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Bordered & Outlined</label>
            <div className="flex flex-wrap gap-2">
              <span className="px-2 py-1 border border-amber-600 text-amber-400 text-xs rounded">Outlined</span>
              <span className="px-2 py-1 border-2 border-amber-600 text-amber-300 text-xs rounded">Bold Border</span>
              <span className="px-2 py-1 border border-dashed border-slate-500 text-slate-300 text-xs rounded">Dashed</span>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<span className="px-2 py-1 border border-amber-600 text-amber-400 rounded">
  Outlined
</span>
<span className="border-2 border-amber-600 text-amber-300">
  Bold
</span>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Gradient Badges</label>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-gradient-to-r from-amber-600 to-blue-600 text-white text-xs rounded-full">Gradient</span>
              <span className="px-3 py-1 bg-gradient-to-r from-amber-600 to-amber-600 text-white text-xs rounded-full">Premium</span>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<span className="px-3 py-1 bg-gradient-to-r from-amber-600 to-blue-600 text-white rounded-full">
  Gradient
</span>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Badge with Close</label>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-900 text-amber-300 text-xs rounded">
                Tag 1
                <button className="ml-1 text-amber-200 hover:text-amber-100"></button>
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-900 text-amber-300 text-xs rounded">
                Tag 2
                <button className="ml-1 text-amber-200 hover:text-amber-100"></button>
              </span>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<span className="inline-flex items-center gap-1 px-2 py-1">
  Tag
  <button></button>
</span>`} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BadgeLabel;


